import { ProgressionState } from "../GameState";
import { Operation } from "../Machine";
import { hasSkill } from "../skill-helpers";
import { ToolId } from "../Tool";
import { PLAN_IDS, PlanId } from "../plans";
import { blueprintFor, ProductBlueprint } from "./blueprint";
import { assemblyOperation } from "./plan-operations";
import {
  PLAN_CATEGORIES,
  PLAN_RECORDS,
  PlanCategoryId,
  PlanRecord,
} from "./plan-record";

export { PLAN_CATEGORIES, planCategoryLabel } from "./plan-record";
export type { PlanCategoryId } from "./plan-record";

/**
 * The bench's plan drawer: every assembly build in the game, filed
 * under a category — the dividers the plan browser shows. This registry
 * exists so the browser can list a plan by skill alone: a build whose
 * driving tool isn't mounted still shows, wearing the tool as a missing
 * supply, instead of vanishing with the tool (Machine.operations only
 * knows what's mounted).
 *
 * A plan selected here may therefore name an operation the station
 * can't currently offer; selectedBenchPlan (tool-work.ts) resolves
 * through this registry for exactly that reason, and the commit path is
 * naturally safe — driving a fastener needs the tool in hand, which
 * needs it mounted.
 */

/** One drawing in the drawer. */
export interface BenchPlan {
  readonly operation: Operation;
  readonly blueprintId: PlanId;
  readonly blueprint: ProductBlueprint;
  readonly record: PlanRecord;
  readonly category: PlanCategoryId;
  /** The pencil note in the drawing's margin. */
  readonly note: string;
  /** The tool whose rail hook the operation lives on, or null for a
   * build every bench knows on its own. */
  readonly sourceToolId: ToolId | null;
}

/**
 * Every assembly plan in the game, in category order (file order within
 * a category). Tool-borne plans keep their tool's id so readiness can
 * ask for it by name.
 */
export const ALL_BENCH_PLANS: ReadonlyArray<BenchPlan> = (() => {
  const order = PLAN_CATEGORIES.map((category) => category.id as string);
  return PLAN_IDS.map((id, index) => {
    const record = PLAN_RECORDS[id];
    return {
      index,
      plan: {
        operation: assemblyOperation(id),
        blueprintId: id,
        blueprint: blueprintFor(id),
        record,
        category: record.category,
        note: record.note,
        sourceToolId: record.offeredBy === "bench" ? null : record.offeredBy,
      } satisfies BenchPlan,
    };
  })
    .sort(
      (a, b) =>
        order.indexOf(a.plan.category) - order.indexOf(b.plan.category) ||
        a.index - b.index,
    )
    .map((entry) => entry.plan);
})();

/** The plans the player's skills unlock — the browser's whole listing.
 * Locked plans stay invisible; there are no grayed-out teasers. */
export function unlockedBenchPlans(
  progression: ProgressionState,
): ReadonlyArray<BenchPlan> {
  return ALL_BENCH_PLANS.filter(
    (plan) =>
      !plan.operation.requiredSkill ||
      hasSkill(progression, plan.operation.requiredSkill),
  );
}

/** The registry row behind an operation id, or null when the id isn't
 * an assembly plan (tool work, a direct-feed cut, "none"). */
export function benchPlanForOperationId(operationId: string): BenchPlan | null {
  return (
    ALL_BENCH_PLANS.find((plan) => plan.operation.id === operationId) ?? null
  );
}

/** The drawing's title for an operation: the plan's own name ("Shelf")
 * for an assembly build, the operation's name for anything else. */
export function planTitle(operation: Operation): string {
  return benchPlanForOperationId(operation.id)?.record.name ?? operation.name;
}
